'use client';

import PageShim from '@/components/PageHeader/PageShim';
import PageTitle from '@/components/PageHeader/PageTitle';
import Box from '@/components/ui/Box';
import Container from '@/components/ui/Container';
import Txt from '@/components/ui/Txt';
import LayoutDefault from '@/layouts/Layout/LayoutDefault';
import LayoutProtectedPasswordForm from '@/layouts/LayoutProtected/LayoutProtectedPasswordForm';
import { Entry } from '@/lib/parsers/entries';
import { maybeGet } from '@liquorice/allsorts-craftcms-nextjs';
import { useTranslations } from 'next-intl';
import React from 'react';
import LayoutProtectedActionsBar from './LayoutProtectedActionsBar';
import useProtectedEntry from './useProtectedEntry';

export type LayoutProtectedProps = React.PropsWithChildren<{
  entry?: Entry;
}>;

const LayoutProtected = ({ entry, children }: LayoutProtectedProps) => {
  const t = useTranslations('protected');
  const { isAuthorized } = useProtectedEntry(entry?.id as string);
  const entryId = entry?.id;
  const maybeProtect = maybeGet(entry, 'protect');

  if (!maybeProtect) return children ?? null;

  if (isAuthorized)
    return (
      <>
        {entryId && <LayoutProtectedActionsBar key="actions" entryId={entryId} />}
        {children ?? null}
      </>
    );

  return (
    <LayoutDefault
      entry={entry}
      slots={{
        header: (
          <Box
            colorSet="neutralLight"
            paper
            cx={{
              marginBottom: '3xl',
              pY: '5xl',
              position: 'relative',
            }}>
            <Container>
              <PageShim />
              <PageTitle variant="h1">{entry?.pageTitle ?? t('title')}</PageTitle>
              <Txt>{entry?.summary ?? t('description')}</Txt>
            </Container>
          </Box>
        ),
        body: (
          <Container cx={{ marginBottom: '3xl' }}>
            {!!entryId && <LayoutProtectedPasswordForm entryId={entryId} />}
          </Container>
        ),
      }}
    />
  );
};

export default LayoutProtected;
